/**
 * Shared medieval theme constants and components used across the Valkenhall UI.
 * Single source of truth for the dark-gold medieval aesthetic.
 */
import { cloneElement } from 'preact';
import { playUI, UI } from '../utils/arena/uiSounds';

/* ── Color tokens ──────────────────────────────────────── */
export const GOLD = 'rgba(180, 140, 60,';
export const GOLD_TEXT = 'rgba(201, 168, 76,';
export const PARCHMENT = 'rgba(228, 213, 160,';
export const PANEL_BG = 'rgba(12, 10, 8, 0.92)';
// Opaque coat for floating lists (card-mention autocomplete). Matches the
// Select dropdown menus; a translucent PANEL_BG over the glass canvas made
// these read as see-through.
export const MENU_BG = '#0e0a06';
export const PANEL_BORDER = `${GOLD} 0.25)`;
export const TEXT_PRIMARY = '#e8d5a0';
export const TEXT_BODY = '#A6A09B';
export const TEXT_MUTED = 'rgba(166, 160, 155, 0.5)';
export const COIN_COLOR = '#f0d060';
export const ACCENT_GOLD = '#d4a843';

// Context-tinted accent. A view that recolours its gold from its
// surroundings (the profile page samples it from the player's
// background art) sets the `--vh-accent-*` custom properties on its
// root and everything built from these tokens follows. Where the
// properties are unset the fallbacks reproduce the gold constants
// above exactly, so shared components read identically elsewhere.
// Same open-prefix shape as GOLD, so the alpha suffix stays put.
export const ACCENT_FALLBACK = {
  '--vh-accent': '180, 140, 60',        // GOLD
  '--vh-accent-bright': '212, 168, 67', // ACCENT_GOLD
  '--vh-accent-hi': '251, 233, 184',    // #fbe9b8 highlight
  '--vh-accent-lo': '185, 137, 58',     // #b9893a shade
  '--vh-accent-deep': '138, 106, 34',   // #8a6a22 deep shade
};
const accentToken = (name) => `rgba(var(${name}, ${ACCENT_FALLBACK[name]}),`;
export const ACCENT = accentToken('--vh-accent');
export const ACCENT_BRIGHT = accentToken('--vh-accent-bright');
export const ACCENT_HI = accentToken('--vh-accent-hi');
export const ACCENT_LO = accentToken('--vh-accent-lo');
export const ACCENT_DEEP = accentToken('--vh-accent-deep');
export const ACCENT_TEXT = `${ACCENT_BRIGHT} 1)`;

// Pearly vintage white for the achievement medallions and their
// completion gauge. Deliberately fixed: on the profile page every
// gold follows the background art, and the awards are the one thing
// that must not. Highlight, body, shade and aged edge of the ramp.
export const PEARL = {
  hi: '#fffdf6',
  body: '#f1e8d6',
  shade: '#d3c7ad',
  edge: '#b3a68b',
};
export const DARK_BASE = '#08080a';

/* ── Atmospheric background ────────────────────────────── */
export const BG_ATMOSPHERE = [
  'radial-gradient(ellipse 80% 50% at 50% 30%, rgba(180,140,60,0.05) 0%, transparent 70%)',
  'radial-gradient(ellipse 60% 40% at 50% 80%, rgba(120,80,30,0.06) 0%, transparent 60%)',
  'radial-gradient(ellipse 100% 100% at 50% 50%, rgba(30,20,8,0.4) 0%, transparent 80%)',
  'radial-gradient(circle at 20% 20%, rgba(100,60,20,0.03) 0%, transparent 40%)',
  'radial-gradient(circle at 80% 70%, rgba(100,60,20,0.03) 0%, transparent 40%)',
  DARK_BASE,
].join(', ');

export const VIGNETTE = 'radial-gradient(ellipse 70% 60% at 50% 50%, transparent 40%, rgba(0,0,0,0.5) 100%)';

/* ── Noise texture for medieval grit ──────────────────── */
const NOISE_SVG = 'url("/tex-noise.webp")';
const NOISE_GOLD_SVG = 'url("/tex-noise-gold.webp")';
/* ── Pre-baked textures (static WebP files, generated by scripts/bake-textures.js) ── */
const STONE_TEX = 'url("/tex-stone.webp")';
const CHISEL_TEX = 'url("/tex-chisel.webp")';
const SCRATCHES_TEX = 'url("/tex-scratches.webp")';
const CRACKS_TEX = 'url("/tex-cracks.webp")';


/* ── Beveled button base ───────────────────────────────── */
// `backgroundBlendMode` softens the stone layer (first in the stack)
// from an opaque overlay into a subtle texture that blends with the
// layers beneath. Keeps the weathered feel without making the stone
// the dominant visual element on every button. Chisel / scratches /
// cracks / noise / gradient stay on `normal` and render unchanged.
export const BEVELED_BTN = {
  background: `${STONE_TEX}, ${CHISEL_TEX}, ${SCRATCHES_TEX}, ${CRACKS_TEX}, ${NOISE_SVG}, linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(0,0,0,0.12) 100%)`,
  backgroundBlendMode: 'soft-light, normal, normal, normal, normal, normal',
  border: `1px solid ${ACCENT} 0.3)`,
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06), inset 0 -1px 0 rgba(0,0,0,0.15), inset 0 0 8px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.4)',
  borderRadius: 'var(--radius-md)',
  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
};


/* ── Dark chip ─────────────────────────────────────────── */
// Compact dark medieval chip. Same dark gradient + gold rim recipe
// as the in-canvas ATK / DEF stat pills (createStatTexture in
// cardMesh.js) but with stone + scratches texture overlays added so a
// DOM-rendered badge picks up the same material weight as the rest of
// the medieval UI. Caller sets dimensions and border-radius; this
// style only fills in the textured background and bevel.
export const DARK_CHIP_STYLE = {
  background: `${STONE_TEX}, ${SCRATCHES_TEX}, linear-gradient(180deg, rgba(22,18,10,0.96) 0%, rgba(8,6,4,0.96) 100%)`,
  backgroundBlendMode: 'soft-light, overlay, normal',
  border: `1px solid ${GOLD} 0.65)`,
  boxShadow: [
    'inset 0 0 0 1px rgba(255,255,255,0.07)',
    '0 2px 8px rgba(0,0,0,0.55)',
  ].join(', '),
};

/* ── Content background gradients ──────────────────────── */
export const CONTENT_BG_DEFAULT = `${STONE_TEX}, ${CHISEL_TEX}, ${SCRATCHES_TEX}, ${CRACKS_TEX}, ${NOISE_SVG}, linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.08) 100%)`;
export const CONTENT_BG_HOVER = `${STONE_TEX}, ${CHISEL_TEX}, ${SCRATCHES_TEX}, ${CRACKS_TEX}, ${NOISE_SVG}, linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(0,0,0,0.04) 100%)`;
export const CONTENT_BG_ACTIVE = `${STONE_TEX}, ${CHISEL_TEX}, ${SCRATCHES_TEX}, ${CRACKS_TEX}, ${NOISE_SVG}, linear-gradient(180deg, rgba(0,0,0,0.06) 0%, rgba(255,255,255,0.02) 100%)`;

/* ── Corner plating tokens ─────────────────────────────── */
// Corner ornament L-shape that hugs every dialog/button corner. Every
// dimension is expressed in design-pixels and rendered in cqw so the
// whole ornament scales as one unit with the canvas. At the 1600×900
// design size 1cqw = 16px, so the values below render identically to
// the legacy fixed-px version, then track the canvas at any other size.
// The arm length, border thickness, radius and inset all scale together,
// keeping the L's proportions and curve geometry constant at every scale.
// The -2 inset matches the thickness so the L lands on the parent's edge.
export const CORNER_SIZE = '0.875cqw';
export const CORNER_THICKNESS = 2;
export const BTN_BORDER = 'rgba(166,160,155,0.3)';
export const BTN_BORDER_HOVER = 'rgba(166,160,155,0.55)';
export const BTN_CORNER = 'rgba(166,160,155,0.5)';
export const BTN_CORNER_HOVER = 'rgba(166,160,155,0.85)';
/* ── Corner-radius tokens ──────────────────────────────── */
// Mirrors the --radius-* ramp in src/index.css so inline styles and
// Tailwind classes share one scale. Pick by ROLE, not by eye.
export const RADIUS = {
  xs: 'var(--radius-xs)',      // hairline details
  sm: 'var(--radius-sm)',      // chips, tab pills
  md: 'var(--radius-md)',      // controls: buttons, inputs, tabs
  lg: 'var(--radius-lg)',      // cards, rows, sub-panels
  xl: 'var(--radius-xl)',      // screen panels, context menus
  '2xl': 'var(--radius-2xl)',  // floating popovers
  '3xl': 'var(--radius-3xl)',  // dialogs
  '4xl': 'var(--radius-4xl)',  // sheets
  full: 'var(--radius-full)',
  // A card, wherever it is drawn. Scales with the card's short side, not
  // the UI ramp; see src/lib/cardCorner.js. Landscape = a site shown
  // horizontally.
  card: 'var(--radius-card)',
  cardLandscape: 'var(--radius-card-landscape)',
};
// Radius for a surface nested `gap` inside a parent of radius `outer`
// (gap = padding + border). Clamped so a deep nest never goes negative.
export function innerRadius(outer, gap) {
  return `max(0px, calc(${outer} - ${gap}))`;
}

/* ── Corner control on a floating panel ────────────────── */
// A control pinned to a panel corner: the chat panel's close arrow, the
// Moments capture button, the Notifications menu's dismiss.
//
// It measures from the panel edge on BOTH axes rather than sitting in a
// header row, because a header row centres it against whatever text is
// beside it and the top inset then has nothing to do with the right one.
// The radius is the panel's corner minus that inset, so the two curves
// stay concentric. Inset and radius have to move together, which is why
// they are derived here instead of written out per call site.
export const PANEL_CORNER_INSET = '0.5cqw';
export const PANEL_CORNER_SIZE = '2cqw';

export function panelCornerStyle(panelRadius = RADIUS['2xl'], inset = PANEL_CORNER_INSET) {
  return {
    position: 'absolute',
    top: inset,
    right: inset,
    width: PANEL_CORNER_SIZE,
    height: PANEL_CORNER_SIZE,
    borderRadius: innerRadius(panelRadius, inset),
  };
}

// Room a header has to leave on its right so its text clears the corner
// control. `panelPadding` is whatever horizontal padding the panel already
// applies to that header, since the control ignores it.
export function panelCornerGutter(panelPadding = '0cqw') {
  return `calc(${PANEL_CORNER_INSET} + ${PANEL_CORNER_SIZE} + 0.375cqw - ${panelPadding})`;
}

export const GOLD_CORNER = `${GOLD} 0.45)`;

/* ── Panel / Dialog styles ─────────────────────────────── */
export const DIALOG_BG = 'linear-gradient(180deg, rgba(25,20,10,0.98) 0%, rgba(15,12,6,0.98) 100%)';
export const DIALOG_BORDER = `${GOLD} 0.3)`;
export const DIALOG_SHADOW = '0 0 60px rgba(0,0,0,0.5), 0 0 30px rgba(180,140,60,0.05)';

export const PANEL_STYLE = {
  background: PANEL_BG,
  border: `1px solid ${PANEL_BORDER}`,
  borderRadius: 'var(--radius-xl)',
};

export const DIALOG_STYLE = {
  background: DIALOG_BG,
  border: `1px solid ${DIALOG_BORDER}`,
  borderRadius: 'var(--radius-3xl)',
  boxShadow: DIALOG_SHADOW,
  // isolation: isolate creates a new stacking context for the dialog
  // panel. This is what lets the embossed Borre knots in FourCorners
  // sit at z-index: -1 (behind static content) without escaping past
  // the panel into the page-level stacking context.
  isolation: 'isolate',
};

/* ── Textured panel (depth + grit, e.g. dice modal) ────── */
// Stone / chisel / scratches / cracks textures overlay on the dark
// panel base, paired with a more saturated gold rim and a layered
// shadow stack: outer drop, soft outer halo, inset gold inner rim,
// and an inset darkening vignette. Together they make the surface
// read as a carved stone tablet with depth, instead of a flat
// rectangle with a border. Matches the FirstPlayerDiceModal recipe.
export const TEXTURED_PANEL_STYLE = {
  background: `${CONTENT_BG_DEFAULT}, ${PANEL_BG}`,
  backgroundBlendMode: 'overlay, normal',
  border: `1px solid ${GOLD} 0.6)`,
  borderRadius: 'var(--radius-xl)',
  boxShadow: [
    '0 32px 100px rgba(0,0,0,0.7)',
    `0 0 80px ${GOLD} 0.18)`,
    `inset 0 0 0 1px ${GOLD} 0.22)`,
    'inset 0 0 60px rgba(0,0,0,0.55)',
  ].join(', '),
  isolation: 'isolate',
};

/* ── Sub-panel (inset / subordinate, sits inside textured) ─ */
// Companion to TEXTURED_PANEL_STYLE for subcomponents that should
// read as "carved into" the parent: same texture for material continuity,
// but inset shadows replace the outer drop and halo so the surface
// looks recessed rather than stacked. Thinner, less saturated gold rim
// reinforces the visual hierarchy. Clearly subordinate.
export const SUB_PANEL_STYLE = {
  background: `${CONTENT_BG_DEFAULT}, ${PANEL_BG}`,
  backgroundBlendMode: 'overlay, normal',
  border: `1px solid ${GOLD} 0.3)`,
  borderRadius: 'var(--radius-lg)',
  boxShadow: [
    'inset 0 2px 4px rgba(0,0,0,0.55)',
    'inset 0 -1px 0 rgba(255,235,195,0.04)',
    `inset 0 0 0 1px ${GOLD} 0.08)`,
    'inset 0 0 28px rgba(0,0,0,0.45)',
  ].join(', '),
};

/* ── Opaque textured panel / sub-panel ─────────────────── */
// TEXTURED_PANEL_STYLE and SUB_PANEL_STYLE sit on PANEL_BG (rgba 0.92),
// which is right for centred modals over a darkened scrim. A panel that
// floats over a live screen needs a fully opaque base or the board reads
// through the stone. These two are that material: same texture stack,
// same rims and shadows, on an opaque near-black.
//
// This is the panel language the Notifications menu and the audio mixer
// popover established; both still carry a local copy of these objects
// and can fold into these exports.
export const OPAQUE_PANEL_BG = '#0e0c08';

export const OPAQUE_TEXTURED_PANEL_STYLE = {
  ...TEXTURED_PANEL_STYLE,
  background: `${CONTENT_BG_DEFAULT}, ${OPAQUE_PANEL_BG}`,
};

export const OPAQUE_SUB_PANEL_STYLE = {
  ...SUB_PANEL_STYLE,
  background: `${CONTENT_BG_DEFAULT}, ${OPAQUE_PANEL_BG}`,
};

/* ── Frosted glass panel ───────────────────────────────── */
// The Arena Hub's panel material, shared so every screen that wants the
// same forged-glass face reads as one surface. The tint is byte-for-byte
// the plate recipe MenuButton uses for Play / Deck Builder / Store: the
// stone / chisel / scratches / cracks / noise set composited normally
// over a near-opaque rgba(12, 10, 8, 0.94) base. Keep the two in sync;
// if the buttons are ever re-toned, the panels have to follow.
//
// `backdropFilter` belongs on the panel itself, not on a full-bleed
// child, and that placement is load-bearing rather than incidental. The
// hub fades its panels in from opacity 0 (`.hub-panel-enter`), and
// `opacity < 1` makes an element a Backdrop Root. Which blinds any
// backdrop-filter on a DESCENDANT for the whole animation, so the frost
// stayed off and then visibly snapped on the instant the animation
// ended. An element forming a backdrop root does not blind ITSELF, so
// with the filter here the blur ramps up smoothly along with the fade.
export const GLASS_PANEL_STYLE = {
  background: `${CONTENT_BG_DEFAULT}, rgba(12, 10, 8, 0.94)`,
  border: `1px solid ${GOLD} 0.3)`,
  borderRadius: 'var(--radius-2xl)',
  boxShadow: [
    'inset 0 1px 0 rgba(255, 240, 200, 0.16)',
    'inset 0 -1px 0 rgba(0, 0, 0, 0.5)',
    '0 8px 32px rgba(0, 0, 0, 0.55)',
  ].join(', '),
  backdropFilter: 'blur(16px) saturate(125%)',
  WebkitBackdropFilter: 'blur(16px) saturate(125%)',
};

/* ── Sheer glass panel (watermark screens) ─────────────── */
// GLASS_PANEL_STYLE with the base coat thinned enough that the
// backdrop blur actually reads as frosted glass. For screens that put
// ornament artwork on the page background (Hall of Fame): the hub
// panels' 0.94 coat would swallow a 15%-opacity watermark whole.
// The 0.875cqw radius (vs the 0.5cqw panel standard) exists so nested
// chrome can stay concentric: a child inset by 0.5cqw keeps the app's
// standard 0.375cqw control radius (outer = inner + gap), e.g. the
// ranked-season search bar.
export const GLASS_PANEL_SHEER_STYLE = {
  ...GLASS_PANEL_STYLE,
  background: `${CONTENT_BG_DEFAULT}, rgba(12, 10, 8, 0.35)`,
  borderRadius: 'var(--radius-2xl)',
};

/* ── Gold primary button ───────────────────────────────── */
export const GOLD_BTN = {
  background: `${STONE_TEX}, ${CHISEL_TEX}, ${SCRATCHES_TEX}, ${CRACKS_TEX}, ${NOISE_GOLD_SVG}, linear-gradient(180deg, ${ACCENT_BRIGHT} 0.9) 0%, ${ACCENT} 0.85) 50%, ${ACCENT_LO} 0.9) 100%)`,
  backgroundBlendMode: 'soft-light, normal, normal, normal, normal, normal',
  border: `1px solid ${ACCENT_HI} 0.55)`,
  borderRadius: 'var(--radius-md)',
  color: '#1a1408',
  boxShadow: `0 0 20px ${ACCENT_BRIGHT} 0.2), inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -1px 0 rgba(0,0,0,0.2), inset 0 0 6px ${ACCENT_DEEP} 0.15)`,
  textShadow: '0 1px 0 rgba(255,255,255,0.15)',
};

/* ── Danger / destructive button ───────────────────────── */
export const DANGER_BTN = {
  background: `${STONE_TEX}, ${CHISEL_TEX}, ${SCRATCHES_TEX}, ${CRACKS_TEX}, ${NOISE_SVG}, linear-gradient(180deg, rgba(180,60,60,0.15) 0%, rgba(120,30,30,0.1) 100%)`,
  backgroundBlendMode: 'soft-light, normal, normal, normal, normal, normal',
  border: `1px solid rgba(180,60,60,0.35)`,
  borderRadius: 'var(--radius-md)',
  color: '#c45050',
  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04), inset 0 -1px 0 rgba(0,0,0,0.12), inset 0 0 6px rgba(0,0,0,0.1)',
  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
};

/* ── Input field ───────────────────────────────────────── */
export const INPUT_STYLE = {
  background: 'rgba(0,0,0,0.25)',
  border: `1px solid ${GOLD} 0.12)`,
  borderRadius: 'var(--radius-sm)',
  color: TEXT_BODY,
  boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
};

/* ── Tab styling helpers ───────────────────────────────── */
// shadcn-inspired pill tabs inside a rounded container. Wrap the row in
// TabBar (ui/TabBar.jsx) so the selection springs between tabs instead of
// each tab fading its own pill, and give the buttons TAB_ACTIVE_TEXT /
// TAB_INACTIVE. TAB_ACTIVE paints the pill on the button itself and is
// only for rows that still render their selection statically.
export const TAB_ACTIVE = {
  color: TEXT_PRIMARY,
  background: `${GOLD} 0.15)`,
  border: 'none',
  borderRadius: 'var(--radius-md)',
  boxShadow: '0 1px 3px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.04)',
  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
};

// Active tab inside a TabBar: the pill travels as its own element, so the
// button contributes only what belongs to the label.
export const TAB_ACTIVE_TEXT = {
  color: TEXT_PRIMARY,
  background: 'transparent',
  border: 'none',
  borderRadius: 'var(--radius-md)',
  textShadow: '0 1px 2px rgba(0,0,0,0.5)',
};

export const TAB_INACTIVE = {
  color: TEXT_MUTED,
  background: 'transparent',
  border: 'none',
  borderRadius: 'var(--radius-md)',
};

// Rounded container that wraps a row of tab buttons.
export const TAB_BAR_STYLE = {
  background: 'rgba(0, 0, 0, 0.35)',
  border: `1px solid ${GOLD} 0.12)`,
  borderRadius: 'var(--radius-lg)',
  padding: '0.125cqw',
};

// Frosted tab bar for the sheer-glass screens. Same shape as
// TAB_BAR_STYLE but with the panels' backdrop blur so it reads as the
// same material instead of a plain see-through box.
export const GLASS_TAB_BAR_STYLE = {
  ...TAB_BAR_STYLE,
  background: 'rgba(12, 10, 8, 0.45)',
  boxShadow: 'inset 0 1px 0 rgba(255, 240, 200, 0.08), 0 4px 16px rgba(0, 0, 0, 0.4)',
  backdropFilter: 'blur(16px) saturate(125%)',
  WebkitBackdropFilter: 'blur(16px) saturate(125%)',
};

/* ── Divider gradient ──────────────────────────────────── */
export const DIVIDER_GRADIENT = `linear-gradient(90deg, transparent 0%, ${GOLD} 0.25) 50%, transparent 100%)`;

/* ── Popover / context menu (in-game) ──────────────────── */
export const POPOVER_STYLE = {
  background: PANEL_BG,
  border: `1px solid ${GOLD} 0.2)`,
  borderRadius: 'var(--radius-2xl)',
  boxShadow: '0 24px 80px rgba(0,0,0,0.4), 0 0 30px rgba(180,140,60,0.04)',
  // Same stacking-context isolation as DIALOG_STYLE so any embossed
  // ornaments (knots, centerpieces) inside a popover stay clipped to
  // the popover instead of escaping to the page level.
  isolation: 'isolate',
};

/* ── Scrollbar accent (css class) ──────────────────────── */
export const SECTION_HEADER_STYLE = {
  color: `${GOLD} 0.55)`,
  textShadow: `0 0 12px ${GOLD} 0.15)`,
};

/* ── Viewport scale factor (for large-screen scaling) ──── */
const HUB_DESIGN_WIDTH = 1600;
const HUB_DESIGN_HEIGHT = 900;

// Legacy scale functions. Neutered as part of the cqw migration. Return 1
// so any remaining `zoom: var(--viewport-scale)` callers no-op. Both these
// functions and their listeners are deleted once every caller is stripped.
export function getViewportScale() {
  return 1;
}

export function getHubViewportScale() {
  return 1;
}

/* ── Shared debounced viewport scale listener ─────────── */
const scaleListeners = new Set();
const hubScaleListeners = new Set();
let scaleRafId = null;

function handleScaleResize() {
  if (scaleRafId) return;
  scaleRafId = requestAnimationFrame(() => {
    scaleRafId = null;
    const scale = getViewportScale();
    document.documentElement.style.setProperty('--viewport-scale', scale);
    for (const fn of scaleListeners) fn(scale);
    const hubScale = getHubViewportScale();
    document.documentElement.style.setProperty('--hub-viewport-scale', hubScale);
    for (const fn of hubScaleListeners) fn(hubScale);
  });
}

if (typeof window !== 'undefined') {
  document.documentElement.style.setProperty('--viewport-scale', String(getViewportScale()));
  document.documentElement.style.setProperty('--hub-viewport-scale', String(getHubViewportScale()));
  window.addEventListener('resize', handleScaleResize);
}

export function onViewportScaleChange(callback) {
  scaleListeners.add(callback);
  return () => scaleListeners.delete(callback);
}

export function onHubViewportScaleChange(callback) {
  hubScaleListeners.add(callback);
  return () => hubScaleListeners.delete(callback);
}

/* ── Helper: alpha-adjust an rgba( prefix string ───────── */
export function adjustAlpha(rgba, alpha) {
  return rgba.replace(/[\d.]+\)$/, `${alpha})`);
}

/* ── Corner plating ornament ───────────────────────────── */
//
// USAGE CONTRACT: render as a DIRECT child of the rounded,
// position:relative container it decorates and pass no `radius`.
// each L inherits the parent's computed per-corner border-radius
// (CSS `inherit` on the corner longhand), so the curve always matches
// the parent's actual rounding, whatever unit or Tailwind class set it,
// even when the parent animates its radius. No per-call-site tuning.
//
// The default inset is -1px, and getting it exactly right is what makes
// the L trace the parent's OUTER stroke instead of drifting off it:
//
//   • The L inherits the parent's corner radius R, so for its arc to be
//     CONCENTRIC with the parent's (one clean curve, not a doubled one)
//     its outer corner must coincide with the parent's outer corner.
//   • But the L is position:absolute, so top/left:0 is the parent's
//     PADDING box. I.e. INSIDE the parent's border. At inset 0 the L
//     sits one border-width inside the outer edge, so its arc curves in
//     tighter than the parent's and reads as a second, inset curve.
//   • Pulling the L out by the border width lands its outer corner on
//     the border-BOX outer edge, exactly where the parent's stroke is.
//     Our panels use a 1px border, hence -1px. A FIXED px (the border
//     is a fixed 1px at every scale), not a scaling cqw value.
//
// For a parent with a thicker border, pass inset = -<borderWidth>px.
//
// `radius` (+ a matching `inset`) remains an escape hatch for ornaments
// that hug an INNER silhouette instead of the parent box (e.g. card
// images with alpha-rounded corners): pass a `radius` matching the
// inner curve and an `inset` that places the L on that ring. Numeric
// `radius`/`inset`/thickness are design-pixels (1cqw = 16px) rendered
// in cqw so the whole ornament scales with the canvas; pass a CSS
// string ('-1px', '0.5cqw') for an explicit unit.
export function CornerPlating({ position, color = GOLD_CORNER, radius = 'inherit', inset = '-1px', size = CORNER_SIZE }) {
  // The L has to be at least as long as the curve it traces, or the
  // bracket ends mid-arc and reads as not following the corner. Pass
  // `size` alongside `radius` on surfaces with a large border-radius.
  const s = size;
  const t = `${CORNER_THICKNESS / 16}cqw`;
  const r = typeof radius === 'number' ? `${radius / 16}cqw` : radius;
  const ins = typeof inset === 'number' ? `${inset / 16}cqw` : inset;
  const base = { zIndex: 2, width: s, height: s, transition: 'border-color 0.2s ease' };
  const styles = {
    'top-left': { ...base, top: ins, left: ins, borderTop: `${t} solid ${color}`, borderLeft: `${t} solid ${color}`, borderTopLeftRadius: r },
    'top-right': { ...base, top: ins, right: ins, borderTop: `${t} solid ${color}`, borderRight: `${t} solid ${color}`, borderTopRightRadius: r },
    'bottom-left': { ...base, bottom: ins, left: ins, borderBottom: `${t} solid ${color}`, borderLeft: `${t} solid ${color}`, borderBottomLeftRadius: r },
    'bottom-right': { ...base, bottom: ins, right: ins, borderBottom: `${t} solid ${color}`, borderRight: `${t} solid ${color}`, borderBottomRightRadius: r },
  };
  return <div className="absolute pointer-events-none" style={styles[position]} data-corner="" />;
}

/* ── Borre knot corner ─────────────────────────────────── */
//
// Small Viking knot tucked into one corner of a panel. Uses CSS
// mask-image so the wrapper div's background-color is what's actually
// rendered. Meaning we can re-tint via the `color` prop without
// shipping multiple SVG variants.
//
// The knot is meant to feel ENGRAVED into the panel, sitting below
// every other content layer:
//
//   1. zIndex: -1 puts it at the bottom of the parent's stacking
//      context. The parent (DIALOG_STYLE / POPOVER_STYLE) sets
//      isolation: isolate so the knot stays clipped to the panel
//      instead of escaping to the page-level stacking context.
//
//   2. The neutral warm-white tint at low opacity blends with the
//      panel texture. The two-stop drop-shadow filter adds a 1px
//      warm highlight (offset down) and a 1px dark recess (offset up),
//      creating the relief that sells the embossed look.
//
//   3. mix-blend-mode: overlay further integrates the knot with
//      whatever's behind it: the strokes lift bright pixels and
//      darken shadows, the way an actual carving would catch light
//      across the panel's stone texture.
//
// The knot's natural orientation has a small upward triangle on top
// and a larger downward V on the bottom. We rotate each corner so the
// V faces INWARD toward the panel center, which feels like the knot
// is "closing" the corner.

// Per-session cache-bust. See VikingOrnament.jsx for the rationale.
// CEF treats CSS mask-image url() as long-lived cached resources, so a
// stable query string per app launch is the most reliable way to make
// edits to the SVG show up after a reload.
const KNOT_URL = `/game-assets/ornaments/viking-borre-knot.svg?v=${typeof Date !== 'undefined' ? Date.now() : 0}`;

const KNOT_ROTATIONS = {
  'top-left': 180,
  'top-right': -90,
  'bottom-left': 90,
  'bottom-right': 0,
};

export function KnotCorner({
  position,
  size = 40,
  inset = 6,
  color = 'rgba(232, 200, 130, 1)',
  opacity = 0.42,
  rotation,
}) {
  const [vertical, horizontal] = position.split('-');
  // The knot is only 180°-symmetric, so the per-corner "V faces inward"
  // map renders two corners 90° off from the other two. Callers that want
  // a uniform frame pass a single `rotation` to override the map.
  const deg = rotation ?? KNOT_ROTATIONS[position];
  // Numeric `size` / `inset` are treated as design-pixels so existing
  // callers passing bare numbers (`size={40}`) get a canvas-tracking value.
  const sz = typeof size === 'number' ? `${size / 16}cqw` : size;
  const ins = typeof inset === 'number' ? `${inset / 16}cqw` : inset;
  return (
    <div
      className="absolute pointer-events-none"
      style={{
        [vertical]: ins,
        [horizontal]: ins,
        width: sz,
        height: sz,
        transform: `rotate(${deg}deg)`,
        maskImage: `url("${KNOT_URL}")`,
        WebkitMaskImage: `url("${KNOT_URL}")`,
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        backgroundColor: color,
        opacity,
        filter: 'drop-shadow(0 1px 0 rgba(255, 235, 195, 0.35)) drop-shadow(0 -1px 0 rgba(0, 0, 0, 0.85))',
        mixBlendMode: 'overlay',
        zIndex: -1,
      }}
      data-knot=""
    />
  );
}

/* ── Four knots shorthand ──────────────────────────────── */
//
// Drop-in companion to FourCorners. Use directly when you want only
// the knots, or pass `knots` to FourCorners to render both.
export function FourKnots({ size = 40, inset = 6, color, opacity, rotation }) {
  return (
    <>
      <KnotCorner position="top-left"     size={size} inset={inset} color={color} opacity={opacity} rotation={rotation} />
      <KnotCorner position="top-right"    size={size} inset={inset} color={color} opacity={opacity} rotation={rotation} />
      <KnotCorner position="bottom-left"  size={size} inset={inset} color={color} opacity={opacity} rotation={rotation} />
      <KnotCorner position="bottom-right" size={size} inset={inset} color={color} opacity={opacity} rotation={rotation} />
    </>
  );
}

/* ── Four corners shorthand ────────────────────────────── */
//
// Drop as a direct child of the rounded container with no `radius`.
// each corner inherits the parent's actual border-radius (see
// CornerPlating's usage contract). Pass `knots` to also render the
// Borre knots in each corner; use those for modal-style panels with
// enough space (typically 320px+ in both dimensions) and skip them on
// tiny things like toasts or loading spinners.
export function FourCorners({
  color = GOLD_CORNER,
  radius,
  size,
  inset = '-1px',
  knots = false,
  knotSize = 40,
  knotInset = 6,
  knotColor,
  knotOpacity,
  knotRotation,
}) {
  return (
    <>
      <CornerPlating position="top-left" color={color} radius={radius} size={size} inset={inset} />
      <CornerPlating position="top-right" color={color} radius={radius} size={size} inset={inset} />
      <CornerPlating position="bottom-left" color={color} radius={radius} size={size} inset={inset} />
      <CornerPlating position="bottom-right" color={color} radius={radius} size={size} inset={inset} />
      {knots ? (
        <FourKnots size={knotSize} inset={knotInset} color={knotColor} opacity={knotOpacity} rotation={knotRotation} />
      ) : null}
    </>
  );
}

/* ── Ornamental divider with celtic rune ───────────────── */
export function OrnamentalDivider({ className }) {
  return (
    <div className={`flex items-center gap-3 select-none ${className || ''}`}>
      <div className="flex-1 h-px" style={{ background: DIVIDER_GRADIENT }} />
      <img src="/rune-divider.webp" alt="" className="h-5" draggable={false} style={{ opacity: 0.25, filter: 'sepia(1) saturate(3) brightness(0.6) hue-rotate(15deg)' }} />
      <div className="flex-1 h-px" style={{ background: DIVIDER_GRADIENT }} />
    </div>
  );
}

/* ── Medieval button component ────────────────────────── */
const VARIANT_SOUND = {
  default: UI.CLICK,
  confirm: UI.CONFIRM,
  cancel: UI.CANCEL,
  danger: UI.CANCEL,
  gold: UI.CONFIRM,
};

const VARIANT_STYLE = {
  default: 'BEVELED_BTN',
  confirm: 'GOLD_BTN',
  cancel: 'BEVELED_BTN',
  danger: 'DANGER_BTN',
  gold: 'GOLD_BTN',
};

export function MedievalBtn({ variant = 'default', sound, onClick, className = '', style: extraStyle, children, ...props }) {
  const baseStyle = variant === 'gold' || variant === 'confirm' ? GOLD_BTN
    : variant === 'danger' ? DANGER_BTN
    : BEVELED_BTN;
  const soundFile = sound === 'none' ? null : (sound || VARIANT_SOUND[variant] || UI.CLICK);

  return (
    <button
      type="button"
      className={`transition-all duration-200 hover:scale-[1.03] active:scale-[0.98] ${className}`}
      style={{ ...baseStyle, ...extraStyle }}
      onClick={(e) => {
        if (soundFile) playUI(soundFile);
        onClick?.(e);
      }}
      {...props}
    >
      {children}
    </button>
  );
}

/* ── Menu-button visual tokens ─────────────────────────── */
//
// A "forged plate" look assembled entirely from layered box-shadows on
// a single element. No nested border divs. Read outward from the
// content:
//
//   1. inset top highlight  → thin lit rim at the top of the plate
//   2. inset bottom shadow  → the plate's lower edge sinking into stone
//   3. inset deep vignette  → pulls focus toward the engraved text
//   4. 0-offset spread #1   → the gold hairline around the plate face
//   5. 0-offset spread #2   → the dark recessed groove (the "gap")
//   6. 0-offset spread #3   → a soft outer warm glint (the plate's outer bevel)
//   7. drop shadow          → grounds the plate against the background
//
// Hover/press swap the stack to warm the hairline and bloom an ember
// inside the plate. No diagonal lighting. The bevel is purely
// top-down, matching the rest of the theme.
// Each variant is a table of the same four visual tokens the button
// animates between. Only the palette changes. The geometry (hairline
// → groove → outer bevel, engraved text, ember bloom) is identical
// across variants so every button in the stack reads as the same
// forged material, just marked with different metal.
const MENU_BTN_VARIANTS = {
  default: {
    shadowIdle: [
      'inset 0 1px 0 rgba(255, 230, 170, 0.10)',
      'inset 0 -1px 0 rgba(0, 0, 0, 0.30)',
      'inset 0 0 22px rgba(0, 0, 0, 0.45)',
      '0 0 0 1px rgba(212, 168, 67, 0.28)',
      '0 0 0 3px rgba(0, 0, 0, 0.55)',
      '0 0 0 4px rgba(180, 140, 60, 0.12)',
      '0 6px 16px rgba(0, 0, 0, 0.55)',
    ].join(', '),
    shadowHover: [
      'inset 0 1px 0 rgba(255, 235, 180, 0.16)',
      'inset 0 -1px 0 rgba(0, 0, 0, 0.30)',
      'inset 0 0 26px rgba(80, 45, 10, 0.35)',
      '0 0 0 1px rgba(232, 195, 110, 0.55)',
      '0 0 0 3px rgba(0, 0, 0, 0.55)',
      '0 0 0 4px rgba(212, 168, 67, 0.30)',
      '0 8px 22px rgba(0, 0, 0, 0.6), 0 0 24px rgba(212, 168, 67, 0.18)',
    ].join(', '),
    shadowPress: [
      'inset 0 2px 3px rgba(0, 0, 0, 0.55)',
      'inset 0 -1px 0 rgba(255, 230, 170, 0.05)',
      'inset 0 0 30px rgba(0, 0, 0, 0.55)',
      '0 0 0 1px rgba(212, 168, 67, 0.40)',
      '0 0 0 3px rgba(0, 0, 0, 0.6)',
      '0 0 0 4px rgba(180, 140, 60, 0.10)',
      '0 2px 6px rgba(0, 0, 0, 0.55)',
    ].join(', '),
    textShadowIdle: [
      '0 -1px 0 rgba(0, 0, 0, 0.9)',
      '0 1px 0 rgba(255, 220, 140, 0.14)',
      '0 2px 5px rgba(0, 0, 0, 0.8)',
    ].join(', '),
    textShadowHover: [
      '0 -1px 0 rgba(0, 0, 0, 0.9)',
      '0 1px 0 rgba(255, 230, 160, 0.22)',
      '0 0 14px rgba(232, 195, 110, 0.35)',
      '0 2px 5px rgba(0, 0, 0, 0.8)',
    ].join(', '),
    ember: 'radial-gradient(ellipse 70% 85% at 50% 55%, rgba(232, 170, 70, 0.18) 0%, rgba(212, 140, 50, 0.08) 40%, transparent 75%)',
    textColorIdle: TEXT_PRIMARY,
    textColorHover: '#f0dca8',
    knotColorIdle: 'rgba(228, 190, 110, 1)',
    knotColorHover: 'rgba(245, 210, 130, 1)',
    knotOpacityIdle: 0.38,
    knotOpacityHover: 0.62,
  },
  danger: {
    shadowIdle: [
      'inset 0 1px 0 rgba(255, 170, 160, 0.06)',
      'inset 0 -1px 0 rgba(0, 0, 0, 0.35)',
      'inset 0 0 26px rgba(0, 0, 0, 0.55)',
      '0 0 0 1px rgba(180, 60, 55, 0.35)',
      '0 0 0 3px rgba(0, 0, 0, 0.6)',
      '0 0 0 4px rgba(140, 40, 40, 0.14)',
      '0 6px 16px rgba(0, 0, 0, 0.55)',
    ].join(', '),
    shadowHover: [
      'inset 0 1px 0 rgba(255, 180, 170, 0.10)',
      'inset 0 -1px 0 rgba(0, 0, 0, 0.35)',
      'inset 0 0 28px rgba(120, 25, 20, 0.45)',
      '0 0 0 1px rgba(220, 95, 85, 0.62)',
      '0 0 0 3px rgba(0, 0, 0, 0.6)',
      '0 0 0 4px rgba(180, 55, 50, 0.36)',
      '0 8px 22px rgba(0, 0, 0, 0.6), 0 0 22px rgba(180, 55, 50, 0.22)',
    ].join(', '),
    shadowPress: [
      'inset 0 2px 3px rgba(0, 0, 0, 0.6)',
      'inset 0 -1px 0 rgba(255, 170, 160, 0.04)',
      'inset 0 0 32px rgba(0, 0, 0, 0.6)',
      '0 0 0 1px rgba(180, 60, 55, 0.5)',
      '0 0 0 3px rgba(0, 0, 0, 0.65)',
      '0 0 0 4px rgba(140, 40, 40, 0.12)',
      '0 2px 6px rgba(0, 0, 0, 0.55)',
    ].join(', '),
    textShadowIdle: [
      '0 -1px 0 rgba(0, 0, 0, 0.9)',
      '0 1px 0 rgba(255, 160, 140, 0.14)',
      '0 2px 5px rgba(0, 0, 0, 0.85)',
    ].join(', '),
    textShadowHover: [
      '0 -1px 0 rgba(0, 0, 0, 0.9)',
      '0 1px 0 rgba(255, 175, 155, 0.22)',
      '0 0 14px rgba(220, 95, 85, 0.4)',
      '0 2px 5px rgba(0, 0, 0, 0.85)',
    ].join(', '),
    ember: 'radial-gradient(ellipse 70% 85% at 50% 55%, rgba(200, 70, 60, 0.22) 0%, rgba(160, 45, 40, 0.10) 40%, transparent 75%)',
    textColorIdle: '#c48888',
    textColorHover: '#e89a95',
    knotColorIdle: 'rgba(200, 110, 90, 1)',
    knotColorHover: 'rgba(230, 135, 115, 1)',
    knotOpacityIdle: 0.34,
    knotOpacityHover: 0.58,
  },
};

// Knot end-cap ornament. Uses the same mask-image + drop-shadow recipe
// as KnotCorner so the knot reads as engraved into the plate face.
function MenuButtonKnot({ side, color, opacity, size = '1.625cqw', inset = '0.625cqw' }) {
  return (
    <div
      data-knot-cap=""
      className="absolute pointer-events-none"
      style={{
        top: '50%',
        [side]: inset,
        width: size,
        height: size,
        transform: 'translateY(-50%)',
        maskImage: `url("${KNOT_URL}")`,
        WebkitMaskImage: `url("${KNOT_URL}")`,
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        backgroundColor: color,
        opacity,
        filter: 'drop-shadow(0 1px 0 rgba(255, 235, 195, 0.35)) drop-shadow(0 -1px 0 rgba(0, 0, 0, 0.85))',
        mixBlendMode: 'overlay',
        transition: 'opacity 80ms ease-out, background-color 80ms ease-out',
        zIndex: 1,
      }}
    />
  );
}

/* ── Ornate menu button (used in hub, game menu, etc.) ──
 *
 * `variant` picks a palette: 'default' is warm gold/bronze, 'danger'
 * is blood-iron (for Quit Game and other destructive entry points).
 * Plate geometry, ornaments, engraved text, and ember bloom are
 * identical across variants. Only the tint shifts.
 *
 * Pass `href` to render as an `<a>` instead of a `<button>`. Used by
 * the Support Development tip-jar so it routes through the main
 * process's setWindowOpenHandler. Pass `icon` to slot a small element
 * (e.g. a lucide-react glyph) ahead of the label inside the plate so
 * external-action buttons read as part of the same forged-plate family
 * instead of needing a bespoke recipe.
 *
 * Pass `aura` to play a finite gold glow pulse over the stroke, icon
 * and label (`.menu-btn-aura` in index.css). The glow layers carry
 * static shadows/filters and only their opacity animates, so the pulse
 * stays on the compositor and never fights the hover handlers, which
 * mutate the plate's own inline styles.
 */
export function MenuButton({
  title, onClick, style: extraStyle, dataTutorial, variant = 'default',
  icon, href, target, rel, compact = false, aura = false,
}) {
  const v = MENU_BTN_VARIANTS[variant] || MENU_BTN_VARIANTS.default;
  const isLink = Boolean(href);
  const Tag = isLink ? 'a' : 'button';
  const tagProps = isLink ? { href, target, rel } : { type: 'button' };
  return (
    <Tag
      {...tagProps}
      data-tutorial={dataTutorial}
      className={`relative w-full text-center cursor-pointer mb-3${isLink ? ' no-underline' : ''}`}
      style={{
        background: `${CONTENT_BG_DEFAULT}, rgba(12, 10, 8, 0.94)`,
        backdropFilter: 'blur(8px)',
        borderRadius: 'var(--radius-md)',
        border: 'none',
        boxShadow: v.shadowIdle,
        transition: 'transform 80ms ease-out, box-shadow 80ms ease-out, background 80ms ease-out',
        transform: 'scale(1)',
        textDecoration: isLink ? 'none' : undefined,
        ...extraStyle,
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget;
        el.style.transform = 'scale(1.015)';
        el.style.boxShadow = v.shadowHover;
        el.style.background = `${CONTENT_BG_HOVER}, rgba(12, 10, 8, 0.94)`;
        const ember = el.querySelector('[data-ember]');
        if (ember) ember.style.opacity = '1';
        const text = el.querySelector('[data-label]');
        if (text) {
          text.style.textShadow = v.textShadowHover;
          text.style.color = v.textColorHover;
        }
        const iconEl = el.querySelector('[data-icon]');
        if (iconEl) iconEl.style.color = v.textColorHover;
        el.querySelectorAll('[data-knot-cap]').forEach((k) => {
          k.style.opacity = String(v.knotOpacityHover);
          k.style.backgroundColor = v.knotColorHover;
        });
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget;
        el.style.transform = 'scale(1)';
        el.style.boxShadow = v.shadowIdle;
        el.style.background = `${CONTENT_BG_DEFAULT}, rgba(12, 10, 8, 0.94)`;
        const ember = el.querySelector('[data-ember]');
        if (ember) ember.style.opacity = '0';
        const text = el.querySelector('[data-label]');
        if (text) {
          text.style.textShadow = v.textShadowIdle;
          text.style.color = v.textColorIdle;
        }
        const iconEl = el.querySelector('[data-icon]');
        if (iconEl) iconEl.style.color = v.textColorIdle;
        el.querySelectorAll('[data-knot-cap]').forEach((k) => {
          k.style.opacity = String(v.knotOpacityIdle);
          k.style.backgroundColor = v.knotColorIdle;
        });
      }}
      onMouseDown={(e) => {
        const el = e.currentTarget;
        el.style.transform = 'scale(0.985)';
        el.style.boxShadow = v.shadowPress;
        el.style.background = `${CONTENT_BG_ACTIVE}, rgba(12, 10, 8, 0.94)`;
      }}
      onMouseUp={(e) => {
        const el = e.currentTarget;
        el.style.transform = 'scale(1.015)';
        el.style.boxShadow = v.shadowHover;
        el.style.background = `${CONTENT_BG_HOVER}, rgba(12, 10, 8, 0.94)`;
      }}
      onClick={(e) => { playUI(variant === 'danger' ? UI.CANCEL : UI.CLICK); onClick?.(e); }}
    >
      {/* Ember glow: soft warm radial that blooms inside the plate on hover. */}
      <div
        data-ember=""
        className="absolute inset-0 pointer-events-none"
        style={{
          background: v.ember,
          borderRadius: 'var(--radius-md)',
          opacity: 0,
          transition: 'opacity 80ms ease-out',
        }}
      />

      {/* Aura ring: re-traces the plate's 1px stroke in bright gold and
          blooms outward. Static box-shadow, opacity driven by the
          .menu-btn-aura keyframes. */}
      {aura ? (
        <div
          aria-hidden="true"
          className="menu-btn-aura absolute inset-0 pointer-events-none"
          style={{
            borderRadius: 'var(--radius-md)',
            boxShadow: [
              '0 0 0 1px rgba(240, 205, 120, 0.85)',
              '0 0 0.75cqw rgba(232, 195, 110, 0.45)',
              '0 0 1.5cqw rgba(212, 168, 67, 0.30)',
            ].join(', '),
          }}
        />
      ) : null}

      <MenuButtonKnot
        side="left"
        color={v.knotColorIdle}
        opacity={v.knotOpacityIdle}
        size={compact ? '1cqw' : '1.625cqw'}
        inset={compact ? '0.5cqw' : '0.625cqw'}
      />
      <MenuButtonKnot
        side="right"
        color={v.knotColorIdle}
        opacity={v.knotOpacityIdle}
        size={compact ? '1cqw' : '1.625cqw'}
        inset={compact ? '0.5cqw' : '0.625cqw'}
      />

      <div
        className={`relative flex items-center justify-center gap-2 ${compact ? 'px-4 py-1.5' : 'px-5 py-3.5'}`}
        style={{ zIndex: 2 }}
      >
        {icon ? (
          <span
            data-icon=""
            className="inline-flex items-center justify-center"
            style={{
              color: v.textColorIdle,
              transition: 'color 80ms ease-out',
            }}
          >
            {icon}
          </span>
        ) : null}
        <span
          data-label=""
          className={`font-bold arena-heading tracking-wide ${compact ? 'text-sm' : 'text-lg'}`}
          style={{
            color: v.textColorIdle,
            textShadow: v.textShadowIdle,
            transition: 'color 80ms ease-out, text-shadow 80ms ease-out',
          }}
        >
          {title}
        </span>

        {/* Aura halo: a blurred bright-gold clone of the icon + label,
            stacked exactly over the originals so the glyphs appear to
            glow through it as its opacity pulses. */}
        {aura ? (
          <div
            aria-hidden="true"
            className="menu-btn-aura absolute inset-0 flex items-center justify-center gap-2 pointer-events-none"
            style={{ color: '#ffe9b0', filter: 'blur(0.15cqw)' }}
          >
            {icon ? (
              <span className="inline-flex items-center justify-center">
                {cloneElement(icon)}
              </span>
            ) : null}
            <span className={`font-bold arena-heading tracking-wide ${compact ? 'text-sm' : 'text-lg'}`}>
              {title}
            </span>
          </div>
        ) : null}
      </div>
    </Tag>
  );
}

/* ── Borre-knot ornament (decorative flourish) ─────────
 * Embossed Viking knot medallion. Used to flank titles, tab bars, and
 * section dividers so the whole UI shares one ornamental vocabulary.
 * Default `side="left"` keeps the knot's natural orientation; pass
 * `side="right"` to mirror it so a matched pair reads as bookends.
 */
export function KnotOrnament({ side = 'left', size = 34, opacity = 0.42, color = 'rgba(232, 195, 110, 1)', className = '', style: extraStyle }) {
  const knotUrl = `/game-assets/ornaments/viking-borre-knot.svg`;
  // Numeric `size` is treated as design-pixels (1cqw = 16px on the 1600
  // design canvas) so the knot scales with the canvas. String values
  // pass through unchanged.
  const sz = typeof size === 'number' ? `${size / 16}cqw` : size;
  return (
    <div
      aria-hidden="true"
      className={className}
      style={{
        width: sz,
        height: sz,
        maskImage: `url("${knotUrl}")`,
        WebkitMaskImage: `url("${knotUrl}")`,
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
        maskSize: 'contain',
        WebkitMaskSize: 'contain',
        backgroundColor: color,
        opacity,
        filter: 'drop-shadow(0 1px 0 rgba(255, 235, 195, 0.35)) drop-shadow(0 -1px 0 rgba(0, 0, 0, 0.85))',
        mixBlendMode: 'overlay',
        transform: side === 'right' ? 'scaleX(-1)' : 'none',
        ...extraStyle,
      }}
    />
  );
}

/* ── Heading watermark (Mammen-style interlace panel) ───
 * Sits on the page background, centered behind a screen's ceremonial
 * heading. Render it inside a `relative` wrapper that shrink-wraps the
 * heading row.
 *
 * `z-index: -1` is what keeps it a watermark. It is taller than the
 * header it is anchored to and spills down over the screen's content,
 * and an absolutely-positioned element otherwise paints ABOVE its
 * ancestors' static in-flow content (CSS painting step 8 vs 4), so
 * without it the interlace lies on top of every panel below. Negative
 * z-index puts it in the nearest ancestor stacking context, just above
 * that element's own background. So the screen root needs to be the
 * stacking context (a `fixed` overlay with a z-index already is), and
 * nothing between it and the wrapper may create one of its own
 * (`opacity < 1`, `transform`, `filter`, `isolation`).
 *
 * Applied as a `mask-image` rather than a `background-image` because the
 * ornament SVG paints itself in `currentColor`, which an externally
 * referenced document can't inherit. The radial layer dissolves the
 * panel's edges into the atmosphere so it reads as a watermark rather
 * than as a cropped rectangle; `mask-composite` keeps only the pixels
 * both layers allow.
 *
 * `height` is in cqw and the width follows the artwork's own trimmed
 * aspect (2108.449 x 3498.773). The 180° rotation points the tendril
 * end down into the page. Anchored to the top of the screen and hung
 * downwards: centring it on the heading put its top half above the
 * canvas, which `.app-canvas { overflow: hidden }` cropped away. Mount
 * it as a direct child of the screen root, not inside the heading row.
 */
const WATERMARK_URL = 'url("/ornaments/viking-mammen-005.svg")';
const WATERMARK_ASPECT = 0.602625;

// Grain tile for the watermark, carved into its MASK rather than painted
// over the gold fill. The shared tex-*.webp overlays top out around
// 9/255 alpha, which is nothing under a 0.22-opacity watermark, so this
// generates its own fractal-noise alpha (0.3 - 1.0) instead. Intersected
// with the ornament mask, it mottles the interlace ribbons so they read
// as weathered inlay instead of flat vector gold.
const WATERMARK_GRAIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="120" height="120">`
  + `<filter id="g" x="0" y="0" width="100%" height="100%">`
  + `<feTurbulence type="fractalNoise" baseFrequency="0.08" numOctaves="4" seed="7" stitchTiles="stitch" />`
  + `<feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.55 0 0 0 0.6" />`
  + `</filter><rect width="120" height="120" filter="url(#g)" /></svg>`;
const WATERMARK_GRAIN = `url("data:image/svg+xml,${encodeURIComponent(WATERMARK_GRAIN_SVG)}")`;

// `hanging`: no fade at the top edge, the knotwork touches the view border
// and fades only to the sides and downward. The element is rotated 180deg,
// so the visual top is the local bottom of the mask.
export function HeadingWatermark({ height = 60, opacity = 0.22, hanging = false }) {
  const falloff = hanging
    ? 'radial-gradient(ellipse 50% 100% at 50% 100%, black 35%, transparent 100%)'
    : 'radial-gradient(closest-side, black 35%, transparent 100%)';
  const mask = `${WATERMARK_URL}, ${WATERMARK_GRAIN}, ${falloff}`;
  const maskSize = '100% 100%, 10cqw 10cqw, 100% 100%';
  const maskRepeat = 'no-repeat, repeat, no-repeat';
  const maskPosition = 'center, center, center';
  return (
    <div
      className="absolute left-1/2 pointer-events-none"
      style={{
        // Hanging: the top of the knot sits past the view edge, so it
        // reads as continuing above the frame rather than starting at it.
        top: hanging ? `-${(height * 0.12).toFixed(2)}cqw` : 0,
        width: `${(height * WATERMARK_ASPECT).toFixed(2)}cqw`,
        height: `${height}cqw`,
        transform: 'translate(-50%, 0) rotate(180deg)',
        background: `${GOLD} 1)`,
        opacity,
        WebkitMaskImage: mask,
        WebkitMaskSize: maskSize,
        WebkitMaskPosition: maskPosition,
        WebkitMaskRepeat: maskRepeat,
        WebkitMaskComposite: 'source-in',
        maskImage: mask,
        maskSize,
        maskPosition,
        maskRepeat,
        maskComposite: 'intersect',
      }}
    />
  );
}

/* ── Settings pill (segmented group / binary toggle) ────
 * Styled entirely via CSS classes in index.css so hover pseudo-states
 * work without per-element handlers. `on` flips between the engraved
 * "off" groove and the raised gold-plate "on" state.
 */
export function SettingsPill({ on, onClick, disabled, children, size = 'md', className = '', ...props }) {
  const pad = size === 'sm' ? 'px-2 py-0.5 text-[0.625cqw]' : 'px-2.5 py-1 text-[0.625cqw]';
  return (
    <button
      type="button"
      disabled={disabled}
      className={`vh-pill ${on ? 'vh-pill-on' : 'vh-pill-off'} ${pad} ${className}`}
      onClick={(e) => { if (!disabled) { playUI(UI.CLICK); onClick?.(e); } }}
      {...props}
    >
      {children}
    </button>
  );
}

/* ── Settings toggle ────────────────────────────────────
 * Physical on/off switch for binary settings. Brass knob slides along
 * a dark rail. Use this for booleans; use `SettingsPill` for mutually
 * exclusive multi-choice groups.
 */
export function SettingsToggle({ on, onClick, disabled, className = '', ...props }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      disabled={disabled}
      className={`vh-toggle ${on ? 'vh-toggle-on' : ''} ${className}`}
      onClick={(e) => { if (!disabled) { playUI(UI.CLICK); onClick?.(e); } }}
      {...props}
    />
  );
}

/* ── Settings slider ────────────────────────────────────
 * Themed range input with a dark carved groove and a brass thumb.
 * `--vh-slider-progress` is set from the current value so the CSS can
 * fill the left portion of the track in gold.
 */
export function SettingsSlider({ value, min = 0, max = 100, step = 1, disabled, onInput, className = '', style: extraStyle, ...props }) {
  const pct = ((value - min) / (max - min)) * 100;
  return (
    <input
      type="range"
      min={min}
      max={max}
      step={step}
      value={value}
      disabled={disabled}
      onInput={onInput}
      className={`vh-slider ${className}`}
      style={{ '--vh-slider-progress': `${pct}%`, ...extraStyle }}
 {...props}
 />
 );
}

/* ── Settings section header ────────────────────────────
 * Small gold all-caps label flanked by fading hairlines. Reads as a
 * carved section-marker inside the settings dialog rather than a flat
 * text line.
 */
export function SettingsSectionHeader({ children, className = '' }) {
 return (
 <div className={`flex items-center gap-2 mb-3 ${className}`}>
      <div
        className="flex-1 h-px"
        style={{ background: `linear-gradient(90deg, transparent 0%, ${GOLD} 0.22) 100%)` }}
      />
      <div
        className="text-[0.625cqw] font-semibold arena-heading"
        style={{
          color: `${GOLD} 0.7)`,
          textShadow: `0 0 10px ${GOLD} 0.25), 0 1px 2px rgba(0,0,0,0.6)`,
        }}
      >
        {children}
      </div>
      <div
        className="flex-1 h-px"
        style={{ background: `linear-gradient(90deg, ${GOLD} 0.22) 0%, transparent 100%)` }}
      />
    </div>
  );
}
